import { ref, computed } from "vue";

type DragData = {
  groupId: string;
  listId: string;
  items: unknown[];
  size: { x: number; y: number };
  dragStartPosition: { x: number; y: number };
  mouseOffset: { x: number; y: number };
  sourceIndex: number;
};

type DragReceiver = {
  listId: string;
  index: number;
  groupData: {
    id: string;
  }
};

const dragPayload = ref<DragData | null>(null);
const dragReceiver = ref<DragReceiver | null>(null);
const dragEndPosition = ref<{ item: { x: number; y: number }, list: { x: number; y: number, id: string } } | null>(null);
const leaveHandlers: ((listId: string) => void)[] = [];

export function useSortableListDrag() {
  function startDrag(dragData: DragData) {
    dragReceiver.value = null;
    dragEndPosition.value = null;
    dragPayload.value = dragData;
  }
  function endDrag() {
    dragPayload.value = null;
    dragReceiver.value = null;
    dragEndPosition.value = null;
    leaveHandlers.length = 0;
  }

  const dragging = computed(() => dragPayload.value !== null);

  function enterList(receiver: DragReceiver) {
    if (dragReceiver.value?.listId === receiver.listId) {
      return;
    }
    if (dragReceiver.value) {
      triggerLeave(dragReceiver.value.listId);
    }
    dragReceiver.value = { ...receiver };
  }
  function updateList(receiver: Partial<DragReceiver>) {
    dragReceiver.value = { ...dragReceiver.value!, ...receiver };
  }
  function leaveList(listId: string) {
    if (dragReceiver.value?.listId === listId) {
      triggerLeave(listId);
      dragReceiver.value = null;
    }
  }
  function clearList() {
    if (dragReceiver.value) {
      triggerLeave(dragReceiver.value.listId);
    }
    dragReceiver.value = null;
  }
  function setEndPosition(pos: { item: { x: number; y: number }, list: { x: number; y: number; id: string } } | null) {
    dragEndPosition.value = pos;
  }

  function getParentLists(element: HTMLElement | null) {
    const lists: string[] = [];
    while (element) {
      if (element.classList.contains("sortable-list")) {
        lists.push(element.id);
      }
      element = element.parentElement;
    }
    return lists;
  }

  function onLeave(handler: (listId: string) => void) {
    leaveHandlers.push(handler);
  }
  function offLeave(handler: (listId: string) => void) {
    const index = leaveHandlers.indexOf(handler);
    if (index !== -1) {
      leaveHandlers.splice(index, 1);
    }
  }
  function triggerLeave(listId: string) {
    for (const handler of leaveHandlers) {
      handler(listId);
    }
  }

  return {
    dragOperation: dragPayload,
    dragReceiver,
    startDrag,
    endDrag,
    dragging,
    enterList,
    updateList,
    leaveList,
    clearList,
    setEndPosition,
    getParentLists,
    dragEndPosition,
    onLeave,
    offLeave
  };
}
